import * as React from "react"
import * as d3 from 'd3'
import { ChartData } from "../subsection"
import { TileGridProps, formatTileGridUnitLabel } from "../../modelling/tile_grid"
import { fitChartWidth, fitChartHeight, resolveChartDefaultWidth } from "./chart_size"

interface HistogramProps {
    chartData: ChartData
    props: TileGridProps | undefined
    cellArea: number
    compact?: boolean
    panelWidth?: number
    panelHeight?: number
    svgId?: string
}

// Helper function to create stable fingerprint for chart data
const createDataFingerprint = (chartData: ChartData): string => {
    if (!chartData.numeric_stats) return "";
    
    try {
        // Only include the specific values that would affect visualization
        const bins = Array.from(chartData.count.keys()).map(Number).sort((a, b) => a - b);
        
        // Create pairs of bin:count that represent the histogram data
        const dataSignature = bins.map(bin => {
            const value = chartData.count.get(bin) || 0;
            return `${bin.toFixed(2)}:${value.toFixed(2)}`;
        }).join('|');
        
        // Include min/max/step to detect axis changes
        const statsSignature = `${chartData.numeric_stats.min.toFixed(2)}-${chartData.numeric_stats.max.toFixed(2)}-${chartData.numeric_stats.step.toFixed(2)}`;
        
        // Add explicit fill type detection
        const fillSignature = chartData.inputFillType || 'none';
        
        return `stats:${statsSignature}|data:${dataSignature}|fill:${fillSignature}`;
    } catch (e) {
        // Fallback if any errors occur
        return "";
    }
};

export const GenerateHistogram = React.memo((props: HistogramProps) => {
    const { chartData, props: tileProps, cellArea, compact, panelWidth, panelHeight, svgId = "hist" } = props;
    const svgRef = React.useRef(null);
    const axesRef = React.useRef(null);
    const [hoveredBar, setHoveredBar] = React.useState<number | null>(null);
    
    // Constants
    const MARGIN = compact
        ? { top: 36, right: 10, bottom: 26, left: 32 }
        : { top: 48, right: 16, bottom: 32, left: 42 };
    const BIN_PADDING = 0;
    const width = fitChartWidth(compact, panelWidth, resolveChartDefaultWidth(compact, panelWidth, 190, 300, 580), 96, panelHeight)
    const height = fitChartHeight(compact, width, 180, 260, 420)
    const [boundsWidth, boundsHeight] = [width - MARGIN.right - MARGIN.left, height - MARGIN.top - MARGIN.bottom];

    // Calculate units and scales
    const maxCount = Math.max(...Array.from(chartData.count.values()));
    const units = maxCount < 0.1 ? "m²" : "km²";
    const { min, max, step } = chartData.numeric_stats ? chartData.numeric_stats : { min: 0, max: 0, step: 0 };
    
    const xScale = d3.scaleLinear()
        .domain([min, max])
        .range([0, boundsWidth]);

    const yScale = d3.scaleLinear()
        .range([boundsHeight, 0])
        .domain([0, units === "km²" ? maxCount : maxCount * (1000 ** 2)]);

    // Calculate the data for the bars once
    const barsData = React.useMemo(() => {
        return Array.from(chartData.count, ([name, value]) => ({ 
            name: Number(name), 
            value: units === "m²" ? value * (1000 ** 2) : value,
            width: xScale(Number(name) + step) - xScale(Number(name)) - BIN_PADDING,
            x: xScale(Number(name)),
            y: yScale(units === "m²" ? value * (1000 ** 2) : value),
            height: boundsHeight - yScale(units === "m²" ? value * (1000 ** 2) : value)
        }));
    }, [chartData.count, step, xScale, yScale, units, boundsHeight]);
    
    // Get color as string
    const getBarColor = (value: number): string => {
        if (chartData.colors && chartData.colors.has(value)) {
            const color = chartData.colors.get(value);
            return color ? "rgb(" + color.toString() + ")" : "#666666";
        }
        return "#666666";
    };
    
    // Draw bars with intelligent updates
    React.useEffect(() => {
        if (!svgRef.current) return;
        
        const svg = d3.select(svgRef.current);
        const t = d3.transition().duration(800);
        
        // Join data to elements (D3's enter/update/exit pattern)
        const bars = svg.selectAll("rect")
            .data(barsData, (d: any) => d.name);
        
        // Enter: Create new bars
        bars.enter()
            .append("rect")
            .attr("fill", d => getBarColor(d.name))
            .attr("stroke", "lightgrey")
            .attr("x", d => d.x)
            .attr("width", d => d.width)
            .attr("y", boundsHeight)
            .attr("height", 0)
            .style("cursor", "pointer")
            .style("transition", "fill 0.2s ease")
            .on("mouseenter", function(event, d) {
                setHoveredBar(d.name);
                // Brighten the color on hover
                const currentColor = getBarColor(d.name);
                const rgbMatch = currentColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (rgbMatch) {
                    const [, r, g, b] = rgbMatch;
                    const brightenedColor = `rgb(${Math.min(255, parseInt(r) + 30)}, ${Math.min(255, parseInt(g) + 30)}, ${Math.min(255, parseInt(b) + 30)})`;
                    d3.select(this).attr("fill", brightenedColor);
                }
            })
            .on("mouseleave", function(event, d) {
                setHoveredBar(null);
                // Restore original color
                d3.select(this).attr("fill", getBarColor(d.name));
            })
            .transition(t)
            .delay((_, i) => i * 20)
            .attr("y", d => d.y)
            .attr("height", d => d.height);
        
        // Update: Update existing bars (but don't override fill for hover effects)
        bars.transition(t)
            .attr("x", d => d.x)
            .attr("width", d => d.width)
            .attr("y", d => d.y)
            .attr("height", d => d.height);
        
        // Ensure all bars (new and existing) have correct fill and hover events
        bars
            .attr("fill", d => getBarColor(d.name)) // Set initial fill
            .style("cursor", "pointer")
            .style("transition", "fill 0.2s ease")
            .on("mouseenter", function(event, d) {
                setHoveredBar(d.name);
                // Brighten the color on hover
                const currentColor = getBarColor(d.name);
                const rgbMatch = currentColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (rgbMatch) {
                    const [, r, g, b] = rgbMatch;
                    const brightenedColor = `rgb(${Math.min(255, parseInt(r) + 30)}, ${Math.min(255, parseInt(g) + 30)}, ${Math.min(255, parseInt(b) + 30)})`;
                    d3.select(this).attr("fill", brightenedColor);
                }
            })
            .on("mouseleave", function(event, d) {
                setHoveredBar(null);
                // Restore original color
                d3.select(this).attr("fill", getBarColor(d.name));
            });
        
        // Exit: Remove bars no longer in the data
        bars.exit()
            .transition(t)
            .attr("y", boundsHeight)
            .attr("height", 0)
            .remove();
            
    }, [barsData, chartData.colors, boundsHeight]);

    // Handle hover text display
    React.useEffect(() => {
        if (!svgRef.current) return;
        
        const svg = d3.select(svgRef.current);
        
        // Remove existing hover text
        svg.selectAll(".hover-text").remove();
        
        if (hoveredBar !== null) {
            const barData = barsData.find(d => d.name === hoveredBar);
            if (barData) {
                svg.append("text")
                    .attr("class", "hover-text")
                    .attr("x", barData.x + barData.width / 2)
                    .attr("y", barData.y - 5)
                    .attr("text-anchor", "middle")
                    .attr("font-size", compact ? "8" : "10")
                    .attr("font-weight", "bold")
                    .attr("fill", "black")
                    .style("pointer-events", "none")
                    .text(`${barData.value.toFixed(2)} ${units}`);
            }
        }
    }, [hoveredBar, barsData, units, compact]);

    // Draw axes
    React.useEffect(() => {
        if (!axesRef.current) return;
        
        const svgElement = d3.select(axesRef.current);
        svgElement.selectAll("*").remove();

        const tickStroke = compact ? 0.8 : 1;
        const tickFs = compact ? "8px" : "10px";

        const xAxisGenerator = d3.axisBottom(xScale);
        const xAxisG = svgElement
            .append("g")
            .attr("transform", `translate(0,${boundsHeight})`);
        xAxisG.call(xAxisGenerator);
        xAxisG.selectAll("text").attr("font-size", tickFs);
        xAxisG.selectAll("line, path").attr("stroke-width", tickStroke);

        const yAxisGenerator = d3.axisLeft(yScale).ticks(compact ? 3 : 4);
        const yAxisG = svgElement.append("g").call(yAxisGenerator);
        yAxisG.selectAll("text").attr("font-size", tickFs);
        yAxisG.selectAll("line, path").attr("stroke-width", tickStroke);
    }, [xScale, yScale, boundsHeight, compact]);

    return (
        <svg id={svgId} width={width} height={height} style={{ marginBottom: 15, overflow: "hidden", display: "block", maxWidth: "100%" }}>
            <g
                width={boundsWidth}
                height={boundsHeight}
                ref={svgRef}
                transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
            />
            <g
                width={boundsWidth}
                height={boundsHeight}
                ref={axesRef}
                transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
            />
            <text
                x={width / 2}
                y={height - (compact ? 3 : 5)}
                textAnchor="middle"
                fontSize={compact ? "11px" : "14px"}
                fill="black"
            >
                {formatTileGridUnitLabel(tileProps, { perArea: true, fallback: "value" })}
            </text>
            <text
                x={-height / 2}
                y={compact ? 11 : 15}
                transform="rotate(-90)"
                textAnchor="middle"
                fontSize={compact ? "11px" : "14px"}
                fill="black"
            >
                {units}
            </text>
        </svg>
    );
}, (prevProps, nextProps) => {
    return (
        createDataFingerprint(prevProps.chartData) === createDataFingerprint(nextProps.chartData) &&
        prevProps.compact === nextProps.compact
    );
});